import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/utils/auth";

export default function CallbackPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, error, handleRedirectCallback } = useAuth();
  const hasHandled = useRef(false);
  const [localError, setLocalError] = useState("");
  const [showRetry, setShowRetry] = useState(false);

  useEffect(() => {
    const runRedirect = async () => {
      if (hasHandled.current) return;
      const params = new URLSearchParams(window.location.search);
      if (params.get("code") && params.get("state")) {
        hasHandled.current = true;
        try {
          await handleRedirectCallback();
          navigate("/", { replace: true });
          return;
        } catch (err) {
          setLocalError(err?.message || "Unable to complete sign in.");
        }
      }
      if (!params.get("code")) {
        navigate("/", { replace: true });
      }
    };
    runRedirect();
  }, [handleRedirectCallback, navigate]);

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      const timer = setTimeout(() => setShowRetry(true), 2500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isAuthenticated, isLoading]);

  return (
    <div className="min-h-screen px-6 py-16" data-testid="callback-page">
      <div className="mx-auto w-full max-w-3xl text-center">
        <h1 className="text-3xl font-bold uppercase" data-testid="callback-title">
          Signing you in...
        </h1>
        <p className="mt-4 text-sm text-muted-foreground" data-testid="callback-subtitle">
          Please wait while we verify your account.
        </p>
        {error || localError ? (
          <p className="mt-4 text-sm text-red-500" data-testid="callback-error">
            {error?.message || localError}
          </p>
        ) : null}
        {showRetry && !isAuthenticated ? (
          <p className="mt-4 text-xs text-muted-foreground" data-testid="callback-retry">
            If this page doesn’t move, refresh and click Log in again.
          </p>
        ) : null}
      </div>
    </div>
  );
}
